import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import AmaLogo from "@/components/app/AmaLogo";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Download, ExternalLink, Printer, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicOutcome() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    axios.get(`${API}/public/outcome/${token}`).then((r) => setData(r.data)).catch((e) => setError(e?.response?.data?.detail || "Link non valido"));
  }, [token]);

  const downloadAtt = async (a) => {
    try {
      const r = await axios.get(`${API}/public/outcome/${token}/attachments/${a.id}`);
      const link = document.createElement("a"); link.href = r.data.data_url; link.download = r.data.filename; link.click();
    } catch { alert("Impossibile scaricare"); }
  };

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "linear-gradient(160deg,#0a0a2e,#3a0e5e)" }}>
      <div className="ama-card p-8 max-w-md text-center">
        <XCircle className="w-10 h-10 text-rose-500 mx-auto mb-3" />
        <div className="font-heading font-bold text-xl">Link non valido</div>
        <div className="text-sm text-slate-600 mt-2">{error}</div>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center text-white bg-[#0a0a2e]">Caricamento…</div>;

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(160deg,#0a0a2e 0%, #3a0e5e 55%, #6f42c1 100%)" }}>
      <div className="max-w-3xl mx-auto p-4 md:p-8 print:bg-white">
        <div className="flex items-center justify-between text-white mb-6 print:hidden">
          <AmaLogo size={40} />
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="outcome-print-btn"><Printer className="w-4 h-4 mr-1" /> Stampa</Button>
        </div>
        <Card className="ama-card border-0 shadow-2xl" data-testid="public-outcome-page">
          <CardHeader>
            <div className="text-xs uppercase tracking-widest text-[#d63384] font-semibold">Comunicazione esito assistenza tecnica</div>
            <CardTitle className="font-heading text-2xl md:text-3xl text-[#0a0a2e]">Pratica {data.case_number}</CardTitle>
            <div className="text-sm text-slate-600">Cliente: <b>{data.customer_company}</b></div>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              <Kv k="Produttore" v={data.item.manufacturer_name} />
              <Kv k="Prodotto" v={`${data.item.product_code} ${data.item.model}`} />
              <Kv k="Seriale originario" v={data.item.serial_number} />
              <Kv k="Seriale restituito/sostituzione" v={data.item.replacement_serial_number || data.item.serial_number} />
              <Kv k="Stato attuale" v={data.item.current_status?.replace(/_/g, " ")} />
              <Kv k="Numero item" v={data.item.item_number} />
            </div>
            <div>
              <div className="text-xs uppercase font-semibold text-slate-500 mb-1">Problema dichiarato</div>
              <div className="bg-fuchsia-50 border border-fuchsia-100 rounded-xl p-4 whitespace-pre-wrap">{data.item.declared_problem}</div>
            </div>
            {data.diagnosis?.diagnosis_text && (
              <div>
                <div className="text-xs uppercase font-semibold text-slate-500 mb-1">Diagnosi del produttore</div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 whitespace-pre-wrap">{data.diagnosis.diagnosis_text}</div>
              </div>
            )}
            {data.intervention && (
              <div className="bg-gradient-to-r from-fuchsia-50 to-purple-50 rounded-xl p-4 border border-fuchsia-100">
                <div className="text-xs uppercase font-semibold text-[#6f42c1]">Intervento eseguito ed esito finale</div>
                <div className="text-lg font-heading font-bold text-[#0a0a2e] mt-1">{data.intervention.final_outcome || data.intervention.intervention_type}</div>
                {data.intervention.description && <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{data.intervention.description}</div>}
              </div>
            )}
            {data.item.customer_visible_notes && (
              <div>
                <div className="text-xs uppercase font-semibold text-slate-500 mb-1">Note per il cliente</div>
                <div className="text-sm text-slate-700 whitespace-pre-wrap">{data.item.customer_visible_notes}</div>
              </div>
            )}
            {data.attachments?.length > 0 && (
              <div>
                <div className="text-xs uppercase font-semibold text-slate-500 mb-2">Documenti allegati</div>
                <div className="space-y-2" data-testid="outcome-attachments">
                  {data.attachments.map((a) => (
                    <div key={a.id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200">
                      <div className="flex-1">
                        <div className="font-semibold text-[#0a0a2e]">{a.display_name}</div>
                        {a.description && <div className="text-xs text-slate-500">{a.description}</div>}
                        <div className="text-[10px] text-slate-400 uppercase tracking-wider">{a.file_type} · {(a.size / 1024).toFixed(0)} KB</div>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => downloadAtt(a)}><Download className="w-4 h-4 mr-1" /> Scarica</Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="text-xs text-slate-500 border-t pt-3">Per informazioni contatti {data.company_name} — {data.company_phone} · {data.company_email}. <br/>{data.slogan}</div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Kv({ k, v }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{k}</div>
      <div className="text-sm text-[#0a0a2e] font-medium">{v || "-"}</div>
    </div>
  );
}
